import * as api from '@/utils/apiClient';
import { storeManagers as initialManagers } from '@/mocks/storesData';
import { articles as initialArticles } from '@/mocks/newsData';
import { managementTeam as fallbackMgmt } from '@/mocks/expertsData';

// ====== Types ======
export interface ManagerRecord {
  id: number;
  name: string;
  position: string;
  city: string;
  store: string;
  experience: string;
  philosophy: string;
  image_prompt: string;
  image_url: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface FoundingMember {
  id: number;
  name: string;
  title: string;
  bio: string;
  tags: string[];
  image_prompt: string;
  image_url: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CoreManagementMember {
  id: number;
  name: string;
  title: string;
  message: string;
  years: string;
  image_prompt: string;
  image_url: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ContentBlock {
  type: 'text' | 'heading' | 'image';
  value: string;
  alt?: string;
}

export interface NewsArticle {
  id: number;
  title: string;
  date: string;
  category: string;
  summary: string;
  image: string;
  featured: boolean;
  content_blocks: ContentBlock[];
  content_html?: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const NEWS_KEY = 'admin_news_articles';
const LAST_SYNC_KEY = 'admin_last_sync_time';

// ====== Auth (使用新 API) ======
export async function login(username: string, password: string): Promise<boolean> {
  await api.login(username, password);
  return true;
}

export function logout() {
  api.logout();
}

export function isAuthenticated(): boolean {
  return api.getAuthToken() !== null;
}

// ====== Utils ======
function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function save(key: string, data: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // storage full or disabled
  }
}

// ====== Store Managers ======
export async function fetchManagers(): Promise<ManagerRecord[]> {
  const data = await api.fetchManagementTeam();
  return data as ManagerRecord[];
}

export async function saveManagersToSupabase(managers: ManagerRecord[]): Promise<boolean> {
  // 函数名保留兼容，实际调用新 API
  const seen = new Map<number, ManagerRecord>();
  for (const m of managers) {
    seen.set(m.id, m);
  }
  const unique = Array.from(seen.values());

  const payload = unique.map((m, i) => ({
    ...m,
    sort_order: m.sort_order ?? i,
    created_at: m.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }));
  await api.saveManagementTeam(payload);
  return true;
}

export async function addManager(manager: Omit<ManagerRecord, 'id' | 'created_at' | 'updated_at'>): Promise<ManagerRecord | null> {
  const all = await fetchManagers();
  const newId = all.length > 0 ? Math.max(...all.map(m => m.id)) + 1 : 1;
  const newManager: ManagerRecord = {
    ...manager,
    id: newId,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  const updated = [...all, newManager];
  await saveManagersToSupabase(updated);
  return newManager;
}

export async function updateManager(id: number, manager: Partial<ManagerRecord>): Promise<boolean> {
  const all = await fetchManagers();
  const updated = all.map(m => m.id === id ? { ...m, ...manager, updated_at: new Date().toISOString() } : m);
  await saveManagersToSupabase(updated);
  return true;
}

export async function deleteManager(id: number): Promise<boolean> {
  const all = await fetchManagers();
  const updated = all.filter(m => m.id !== id);
  await saveManagersToSupabase(updated);
  return true;
}

// ====== 旧的迁移函数（保留空实现，避免调用方报错） ======
export async function migrateLocalToSupabase(): Promise<boolean> {
  // 数据已迁移到 MySQL，此函数已无需执行
  return true;
}

// ====== News Articles ======
export async function fetchArticles(): Promise<NewsArticle[]> {
  const data = await api.fetchArticles();
  return (data as NewsArticle[]).map((a) => ({
    ...a,
    content_blocks: Array.isArray(a.content_blocks) ? a.content_blocks : [],
  }));
}

export async function saveArticlesToSupabase(articles: NewsArticle[]): Promise<boolean> {
  // 与原 Supabase 实现保持相同的语义：差异化 INSERT/UPDATE/DELETE
  const seen = new Map<number, NewsArticle>();
  for (const a of articles) {
    seen.set(a.id, a);
  }
  const unique = Array.from(seen.values());

  // 1. 获取数据库中现有文章
  const existing = await api.fetchArticles();
  const existingIds = new Set(existing.map(a => a.id));
  const currentIds = new Set(unique.map(a => a.id));

  // 2. 删除不再存在的文章
  const toDelete = existing.filter(a => !currentIds.has(a.id));
  for (const a of toDelete) {
    await api.deleteArticle(a.id);
  }

  // 3. 插入新文章 / 更新已存在文章
  for (let i = 0; i < unique.length; i++) {
    const a = unique[i];
    const payload = {
      title: a.title,
      date: a.date,
      category: a.category,
      summary: a.summary,
      image: a.image,
      featured: a.featured,
      content_blocks: a.content_blocks,
      content_html: a.content_html || '',
      sort_order: a.sort_order ?? i,
    };

    if (existingIds.has(a.id)) {
      await api.updateArticle(a.id, payload);
    } else {
      // 新文章不传 id，让数据库自增
      const { id: _id, ...payloadWithoutId } = { id: a.id, ...payload };
      await api.createArticle(payloadWithoutId as never);
    }
  }

  return true;
}

export async function addArticle(article: Omit<NewsArticle, 'created_at' | 'updated_at'>): Promise<NewsArticle | null> {
  const created = await api.createArticle(article as never);
  return created as NewsArticle;
}

export async function updateArticle(id: number, article: Partial<NewsArticle>): Promise<boolean> {
  await api.updateArticle(id, article as never);
  return true;
}

export async function deleteArticle(id: number): Promise<boolean> {
  await api.deleteArticle(id);
  return true;
}

export async function migrateNewsToSupabase(): Promise<boolean> {
  // 数据已迁移到 MySQL，此函数已无需执行
  return true;
}

// ====== Legacy News (localStorage fallback) ======
export function getArticles() {
  const data = load(NEWS_KEY, initialArticles);
  return [...data].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function saveArticles(data: typeof initialArticles) {
  save(NEWS_KEY, data);
}

// ====== Sync Timestamp ======
export function getLastSyncTime(): string | null {
  try {
    const raw = localStorage.getItem(LAST_SYNC_KEY);
    return raw || null;
  } catch {
    return null;
  }
}

export function updateLastSyncTime(): string {
  const now = new Date().toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  localStorage.setItem(LAST_SYNC_KEY, now);
  return now;
}

// ====== Legacy localStorage functions (for backward compat) ======
export function getManagers() {
  return load('admin_store_managers_v3', initialManagers);
}

export function saveManagers(data: typeof initialManagers) {
  save('admin_store_managers_v3', data);
}

// ====== Founding Team ======
export async function fetchFoundingMembers(): Promise<FoundingMember[]> {
  const data = await api.fetchFoundingTeam();
  return data.map((m) => ({
    ...m,
    tags: Array.isArray(m.tags) ? m.tags : [],
  })) as FoundingMember[];
}

export async function saveFoundingMembersToSupabase(members: FoundingMember[]): Promise<boolean> {
  const seen = new Map<number, FoundingMember>();
  for (const m of members) {
    seen.set(m.id, m);
  }
  const unique = Array.from(seen.values());

  const payload = unique.map((m, i) => ({
    ...m,
    sort_order: m.sort_order ?? i,
    created_at: m.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }));
  await api.saveFoundingTeam(payload);
  return true;
}

export async function addFoundingMember(member: Omit<FoundingMember, 'id' | 'created_at' | 'updated_at'>): Promise<FoundingMember | null> {
  const all = await fetchFoundingMembers();
  const newId = all.length > 0 ? Math.max(...all.map(m => m.id)) + 1 : 1;
  const newMember: FoundingMember = {
    ...member,
    id: newId,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  const updated = [...all, newMember];
  await saveFoundingMembersToSupabase(updated);
  return newMember;
}

export async function updateFoundingMember(id: number, member: Partial<FoundingMember>): Promise<boolean> {
  const all = await fetchFoundingMembers();
  const updated = all.map(m => m.id === id ? { ...m, ...member, updated_at: new Date().toISOString() } : m);
  await saveFoundingMembersToSupabase(updated);
  return true;
}

export async function deleteFoundingMember(id: number): Promise<boolean> {
  const all = await fetchFoundingMembers();
  const updated = all.filter(m => m.id !== id);
  await saveFoundingMembersToSupabase(updated);
  return true;
}

// ====== Core Management Team ======
export async function fetchCoreManagementMembers(): Promise<CoreManagementMember[]> {
  const data = await api.fetchCoreManagementTeam();
  return data as CoreManagementMember[];
}

export async function saveCoreManagementMembersToSupabase(members: CoreManagementMember[]): Promise<boolean> {
  const seen = new Map<number, CoreManagementMember>();
  for (const m of members) {
    seen.set(m.id, m);
  }
  const unique = Array.from(seen.values());

  const payload = unique.map((m, i) => ({
    ...m,
    sort_order: m.sort_order ?? i,
    created_at: m.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }));
  await api.saveCoreManagementTeam(payload);
  return true;
}

export async function addCoreManagementMember(member: Omit<CoreManagementMember, 'id' | 'created_at' | 'updated_at'>): Promise<CoreManagementMember | null> {
  const all = await fetchCoreManagementMembers();
  const newId = all.length > 0 ? Math.max(...all.map(m => m.id)) + 1 : 1;
  const newMember: CoreManagementMember = {
    ...member,
    id: newId,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  const updated = [...all, newMember];
  await saveCoreManagementMembersToSupabase(updated);
  return newMember;
}

export async function updateCoreManagementMember(id: number, member: Partial<CoreManagementMember>): Promise<boolean> {
  const all = await fetchCoreManagementMembers();
  const updated = all.map(m => m.id === id ? { ...m, ...member, updated_at: new Date().toISOString() } : m);
  await saveCoreManagementMembersToSupabase(updated);
  return true;
}

export async function deleteCoreManagementMember(id: number): Promise<boolean> {
  const all = await fetchCoreManagementMembers();
  const updated = all.filter(m => m.id !== id);
  await saveCoreManagementMembersToSupabase(updated);
  return true;
}

// ====== 图片上传 ======
export async function uploadImageToStorage(
  file: File,
  _folder: 'covers' | 'blocks'
): Promise<string | null> {
  if (!file.type.startsWith('image/')) return null;
  if (file.size > 5 * 1024 * 1024) return null;

  const url = await api.uploadImage(file);
  return url;
}
